import { useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { MARK_CIRCLE_READ } from '../gql/query'
import { MarkCircleReadResponse, MarkCircleReadVariables } from '../gql/types'

/**
 * Returns a fire-and-forget "mark this circle read up to `at`" function — a
 * server-authoritative read-position advance via `markCommunityFeedRead`.
 *
 * Used when the drawer CLOSES. The chat-side mark-read is throttled (15s), so
 * the last position seen before close can be stranded, leaving the nav badge
 * above 0 for messages already read. Firing once on close flushes it.
 *
 * `at` is REQUIRED here rather than optional-with-a-now-default: closing the
 * drawer is not evidence that anything was read, and omitting it would tell the
 * server "everything as of now" — silently clearing the badge for messages the
 * viewer never saw, which is exactly what the visible-scroll read model exists
 * to prevent. The caller passes its stored visible position and skips the call
 * when there is none. Errors are swallowed — a failed mark-read must never break
 * closing the drawer; the next open reconciles.
 */
export default function useMarkCircleReadOnClose (jwt?: string | null) {
  const [markRead] = useMutation<MarkCircleReadResponse, MarkCircleReadVariables>(MARK_CIRCLE_READ)
  return useCallback((feedId: string, at: string): Promise<unknown> => {
    if (!feedId || !at) return Promise.resolve()
    return markRead({ variables: { feedId, at }, context: { jwt } }).catch(() => undefined)
  }, [markRead, jwt])
}
